import React, { useEffect, useState } from "react";
import { CheckIcon, TrashIcon } from "@radix-ui/react-icons";
import { Heading, Flex, Card, Button, Text, Grid, Theme, Box, Badge, Spinner } from "@radix-ui/themes";
import { createThemePreferencesProperties, deleteThemePreferencesProperties, updateThemePreferencesProperties } from "./ThemePreferenceProperties";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useForm, useWatch } from "react-hook-form";
import { useThemePreferencesProperties } from "./UseThemePreferences";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { Theme_Preferences } from "../../utils/constants";


export const ColorOptions = [
  { id: "gray", description: "Cinza" }, { id: "gold", description: "Dourado" }, { id: "bronze", description: "Bronze" },
  { id: "brown", description: "Castanho" }, { id: "yellow", description: "Amarelo" }, { id: "amber", description: "Âmbar" },
  { id: "orange", description: "Laranja" }, { id: "tomato", description: "Tomate" }, { id: "red", description: "Vermelho" },
  { id: "ruby", description: "Rubi" }, { id: "crimson", description: "Carmesim" }, { id: "pink", description: "Rosa" },
  { id: "plum", description: "Ameixa" }, { id: "purple", description: "Roxo" }, { id: "violet", description: "Violeta" },
  { id: "iris", description: "Íris" }, { id: "indigo", description: "Índigo" }, { id: "blue", description: "Azul" },
  { id: "cyan", description: "Ciano" }, { id: "teal", description: "Verde-azulado" }, { id: "jade", description: "Jade" },
  { id: "green", description: "Verde" }, { id: "grass", description: "Verde relva" }, { id: "lime", description: "Lima" },
  { id: "mint", description: "Menta" }, { id: "sky", description: "Celeste" },
] as const;

export const GrayOptions = [
  { id: "auto", description: "Automático" }, { id: "gray", description: "Padrão" }, { id: "mauve", description: "Malva" },
  { id: "slate", description: "Ardósia" }, { id: "sage", description: "Sálvia" }, { id: "olive", description: "Oliva" },
  { id: "sand", description: "Areia" },
] as const;

export const RadiusOptions = [
  { id: "none", description: "Sem bordas" }, { id: "small", description: "Pequeno" },
  { id: "medium", description: "Médio" }, { id: "large", description: "Grande" }, { id: "full", description: "Completo" },
] as const;

const DEFAULTS = {
  appearance: "",
  accentColor: "",
  grayColor: "",
  radius: "",
  scaling: "",
};

export const ThemeOptionsScreen = ({ userId }: { userId?: string }) => {
  const { data: themeData, isLoading, refetch } = useThemePreferencesProperties();
  const queryClient = useQueryClient();
  const [saveAction, setSaveAction] = useState<any>(null);
  const [updateAction, setUpdateAction] = useState<any>(null);
  const [removeAction, setRemoveAction] = useState<any>(null);

  const mutationCreate = useMutation({
    mutationFn: createThemePreferencesProperties,
    mutationKey: "createThemePreferences",
  });
  const mutationUpdate = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      updateThemePreferencesProperties(id, body),
    mutationKey: "updateThemePreferences",
  });
  const mutationDelete = useMutation({
    mutationFn: deleteThemePreferencesProperties,
    mutationKey: "deleteThemePreferences",
  });

  const isSubmitting =
    mutationCreate.isLoading || mutationUpdate.isLoading || mutationDelete.isLoading;

  const { control, formState: { errors }, reset, watch } = useForm({
    defaultValues: DEFAULTS
  });
const previewTheme = useWatch({ control });


  useEffect(() => {
    if (themeData) {
      reset({
        appearance: themeData.appearance || DEFAULTS.appearance,
        accentColor: themeData.accentColor || DEFAULTS.accentColor,
        grayColor: themeData.grayColor || DEFAULTS.grayColor,
        radius: themeData.radius || DEFAULTS.radius,
        scaling: themeData.scaling || DEFAULTS.scaling,
      });
    }
  }, [themeData, reset]);

  
  const handleSave = () => {
    const formValues = watch();
    
    if (themeData?.id) {
      setUpdateAction(formValues);
    } else {
      setSaveAction(formValues);
    }
  };

  
  const confirmSave = async () => {
    if (!saveAction) return;
    try {
      const body = { userId: userId!, ...saveAction };
      await mutationCreate.mutateAsync(body);
      // Mantém no ecrã o que foi submetido, e só depois ressincroniza a cache —
      // partilhada com o `ThemeProvider`, pelo que o tema da aplicação inteira
      // muda sem refresh.
      reset(saveAction);
      toast.success("Tema guardado com sucesso!");
      await refetch();
    } catch (e: any) {
      toast.error(`Erro ao guardar: ${e?.message ?? e}`);
    } finally {
      setSaveAction(null);
    }
  };


  const confirmUpdate = async () => {
    if (!themeData?.id || !updateAction) return;

    try {
      const body = { userId: userId!, ...updateAction };
      await mutationUpdate.mutateAsync({ id: themeData.id, body });
      reset(updateAction);
      toast.success("Tema actualizado com sucesso!");
      await refetch();
    } catch (e: any) {
      toast.error(`Erro ao actualizar: ${e?.message ?? e}`);
    } finally {
      setUpdateAction(null);
    }
  };


  const handleDelete = () => {
    if (!themeData?.id) return;
    setRemoveAction(themeData);
  };


  const confirmRemove = async () => {
    if (!removeAction?.id) return;
    try {
      await mutationDelete.mutateAsync(removeAction.id);
      reset(DEFAULTS);
      toast.success("Preferências removidas com sucesso!");
      // `removeQueries` e não `refetch`: se o GET seguinte falhar (o registo já
      // não existe), o React Query mantinha o `themeData` anterior em cache e o
      // botão continuaria a dizer "Actualizar" sobre preferências apagadas.
      queryClient.removeQueries(Theme_Preferences);
    } catch (e: any) {
      toast.error(`Erro ao remover: ${e?.message ?? e}`);
    } finally {
      setRemoveAction(null);
    }
  };

  if (isLoading) return (
    <Flex align="center" justify="center" gap="2" p="5">
      <Spinner loading />
      <Text color="gray">A processar...</Text>
    </Flex>
  );

  return (
    <>
      <Flex direction="column" align="center" gap="4" p="6">
        <Heading size="6" mb="2">Configurações de Tema</Heading>

        <Grid columns={{ initial: '1', md: '2' }} gap="6" style={{ maxWidth: 1000, width: '100%' }}>

            <Card size="3" style={{ height: '100%' }}>
            <Heading size="4" mb="4">Personalizar</Heading>

            <Flex direction="column" gap="3">
            <SelectComponent
              required={true}
              name="appearance"
              label="Aparência"
              control={control}
              errors={errors}
              options={[
                { value: "inherit", label: "Herdar" },
                { value: "light", label: "Claro" },
                { value: "dark", label: "Escuro" }
              ]}
              style={{ width: '100%' }}
            />
            
            <SelectComponent
              required={true}
              name="accentColor"
              label="Cor de Destaque"
              control={control}
              errors={errors}
              options={ColorOptions.map(o => ({ value: o.id, label: o.description }))}
              style={{ width: '100%' }}
            />
            
            <SelectComponent
              required={true}
              name="grayColor"
              label="Tons de Cinzento"
              control={control}
              errors={errors}
              options={GrayOptions.map(o => ({ value: o.id, label: o.description }))}
              style={{ width: '100%' }}
            />
            
            <SelectComponent
              required={true}
              name="radius"
              label="Bordas"
              control={control}
              errors={errors}
              options={RadiusOptions.map(o => ({ value: o.id, label: o.description }))}
              style={{ width: '100%' }}
            />
            
            <SelectComponent
              required={true}
              name="scaling"
              label="Escala"
              control={control}
              errors={errors}
              options={[
                { value: "90%", label: "90%" },
                { value: "95%", label: "95%" },
                { value: "100%", label: "100%" },
                { value: "105%", label: "105%" },
                { value: "110%", label: "110%" }
              ]}
              style={{ width: '100%' }}
            />
            </Flex>

            <Flex justify="end" mt="5" gap="3">
              <Button
                type="button"
                color="red"
                variant="soft"
                onClick={handleDelete}
                disabled={!themeData?.id || isSubmitting}
              >
                <TrashIcon /> Remover
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Spinner loading>
                    <CheckIcon />
                  </Spinner>
                ) : (
                  <CheckIcon />
                )}
                {themeData?.id ? "Actualizar" : "Guardar"}
              </Button>
            </Flex>
          </Card>

          
          <Theme 
            appearance={previewTheme.appearance as any || "light"}
            accentColor={previewTheme.accentColor as any || "indigo"}
            grayColor={previewTheme.grayColor as any || "gray"}
            radius={previewTheme.radius as any || "medium"}
            scaling={previewTheme.scaling as any || "100%"}
          >
            <Card size="3" style={{ height: '100%' }}>
              <Heading size="4" mb="4">Pré-visualização</Heading>

              <Flex direction="column" gap="4">
                <Box>
                  <Text as="div" size="2" weight="bold" mb="2">
                    Aparência: {previewTheme.appearance || "light"}
                  </Text>
                  <Text as="p">
                    Este é um exemplo de texto normal no tema seleccionado.
                    Pode ver como as cores e os estilos aparecem.
                  </Text>
                </Box>

              
                <Flex gap="2" wrap="wrap">
                  <Button type="button" size="2">Botão Primário</Button>
                  <Button type="button" size="2" variant="soft">Botão Suave</Button>
                  <Button type="button" size="2" variant="outline">Botão Contornado</Button>
                </Flex>

                <Card size="2" style={{ backgroundColor: 'var(--gray-3)' }}>
                  <Text as="div" size="2" weight="bold" mb="2">
                    Cartão Aninhado
                  </Text>
                  <Text as="p" size="1">
                    Os cartões e contentores usam os tons de cinzento seleccionados.
                  </Text>
                </Card>

                <Flex direction="column" gap="2">
                  <Text size="1" color="gray">Texto secundário em cinzento</Text>
                  <Text size="3" weight="bold">Texto em destaque</Text>
                  <Text size="2">Texto de tamanho médio</Text>
                </Flex>

                <Box p="3" style={{
                  border: '2px solid var(--accent-9)',
                  borderRadius: 'var(--radius-3)'
                }}>
                  <Text size="2">
                    Bordas com raio: <strong>{previewTheme.radius || "medium"}</strong>
                  </Text>
                </Box>

                <Flex gap="2" align="center">
                  <Button type="button" size="1" variant="soft">Pequeno</Button>
                  <Button type="button" size="2" variant="soft">Médio</Button>
                  <Button type="button" size="3" variant="soft">Grande</Button>
                </Flex>
              </Flex>
            </Card>
          </Theme>
        </Grid>
      </Flex>

      <ConfirmAlertDialog
        open={!!saveAction}
        onOpenChange={(open: any) => !open && setSaveAction(null)}
        title="Guardar configuração"
        description="As preferências de tema serão aplicadas à aplicação."
        confirmLabel="Guardar"
        confirmColor="green"
        disabledConfirm={isSubmitting}
        item={saveAction}
        onConfirm={confirmSave}
        onCancel={() => setSaveAction(null)}
      />

      <ConfirmAlertDialog
        open={!!updateAction}
        onOpenChange={(open: any) => !open && setUpdateAction(null)}
        title="Actualizar configuração"
        description="As preferências de tema actuais serão substituídas."
        confirmLabel="Actualizar"
        confirmColor="blue"
        disabledConfirm={isSubmitting}
        item={updateAction}
        onConfirm={confirmUpdate}
        onCancel={() => setUpdateAction(null)}
      />

      <ConfirmAlertDialog
        open={!!removeAction}
        onOpenChange={(open: any) => !open && setRemoveAction(null)}
        title="Remover configuração"
        description="A aplicação voltará ao tema por omissão. Pretende continuar?"
        confirmLabel="Remover"
        disabledConfirm={isSubmitting}
        item={removeAction}
        onConfirm={confirmRemove}
        onCancel={() => setRemoveAction(null)}
      />
    </>
  );
};